import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    ArrowLeft, Mail, MessageSquare, Phone, Edit3, Plus, Trash2, UserCheck, FileText, X,
} from "lucide-react";
import LeadTypePill from "@/components/LeadTypePill";
import LeadTypesPickerDialog from "@/components/LeadTypesPickerDialog";
import ConvertLeadDialog from "@/components/ConvertLeadDialog";
import CopyableField from "@/components/CopyableField";
import UserAssignSelect from "@/components/UserAssignSelect";
import MentionTextarea from "@/components/MentionTextarea";
import { LeadStatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { leadPhoneDisplay } from "@/lib/phone";
import { DirectionBadge } from "@/components/comms/DirectionBadge";
import { CommMessageCell, CommDetails, commDetailItems } from "@/components/comms/CommMessageCell";

const LEAD_STATUSES = ["new_lead", "paid_lead", "follow_up", "shark_tank", "hot_lead"];
const LEAD_STATUS_LABEL = {
    new_lead: "New Lead", paid_lead: "Paid Lead", follow_up: "Follow Up",
    shark_tank: "Shark Tank", hot_lead: "Hot Lead",
    // Legacy compat (read-only display)
    new: "New Lead", contacted: "Contacted", responded: "Responded",
    converted: "Converted", archived: "Archived",
};

export default function LeadDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [types, setTypes] = useState([]);
    const [noteList, setNoteList] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [newNoteMentions, setNewNoteMentions] = useState([]);
    const [editingNote, setEditingNote] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get("tab") || "overview");

    // Sync URL ?tab=… so notification deep-links land on the right tab.
    useEffect(() => {
        const wanted = searchParams.get("tab");
        if (wanted && wanted !== tab) setTab(wanted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const current = searchParams.get("tab");
        if (tab !== current) {
            const next = new URLSearchParams(searchParams);
            if (tab === "overview") next.delete("tab"); else next.set("tab", tab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const mentionCandidates = useMemo(() => {
        const _lead = data?.lead;
        if (!_lead) return [];
        const list = [];
        if (_lead.processor_user_id && _lead.processor_name) {
            list.push({ id: _lead.processor_user_id, name: _lead.processor_name, email: _lead.processor_email, label: `Processor — ${_lead.processor_email || ""}` });
        }
        if (_lead.sales_rep_user_id && _lead.sales_rep_name && _lead.sales_rep_user_id !== _lead.processor_user_id) {
            list.push({ id: _lead.sales_rep_user_id, name: _lead.sales_rep_name, email: _lead.sales_rep_email, label: `Sales Rep — ${_lead.sales_rep_email || ""}` });
        }
        return list;
    }, [data]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);
    const [editOverview, setEditOverview] = useState(false);
    const [overviewDraft, setOverviewDraft] = useState({});

    const load = useCallback(async () => {
        const [d, t] = await Promise.all([api.get(`/leads/${id}`), api.get("/lead-types")]);
        setData(d.data);
        setTypes(t.data);
    }, [id]);

    const loadNotes = useCallback(async () => {
        const r = await api.get(`/leads/${id}/notes`);
        setNoteList(r.data || []);
    }, [id]);

    useEffect(() => { load(); loadNotes(); }, [id, load, loadNotes]);

    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

    const { lead, timeline, campaigns } = data;
    const leadTypeIds = lead.lead_type_ids || [];
    const leadTypeObjs = leadTypeIds.map(tid => types.find(t => t.id === tid)).filter(Boolean);

    const persist = async (patch, success = "Saved") => {
        try {
            await api.put(`/leads/${id}`, { ...lead, ...patch });
            toast.success(success);
            await load();
            return true;
        } catch (e) { toast.error(formatApiError(e)); return false; }
    };

    const saveTypes = async (ids) => persist({ lead_type_ids: ids }, "Lead types updated");
    const updateStatus = async (v) => persist({ lead_status: v }, "Status updated");

    const startEditOverview = () => {
        setOverviewDraft({
            case_id: lead.case_id || "",
            first_name: lead.first_name || "",
            last_name: lead.last_name || "",
            dob: lead.dob || "",
            ssn_last4: lead.ssn_last4 || "",
            lead_status: lead.lead_status || "new_lead",
            source: lead.source || "",
            campaign_source: lead.campaign_source || "",
            assigned_rep: lead.assigned_rep || "",
            processor_user_id: lead.processor_user_id || null,
            processor_name: lead.processor_name || "",
            processor_email: lead.processor_email || "",
            sales_rep_user_id: lead.sales_rep_user_id || null,
            sales_rep_name: lead.sales_rep_name || "",
            sales_rep_email: lead.sales_rep_email || "",
            phone_raw: lead.phone_raw || "",
            alternate_phone: lead.alternate_phone || "",
            email: lead.email || "",
            alternate_email: lead.alternate_email || "",
            preferred_contact_method: lead.preferred_contact_method || "",
            best_time_to_contact: lead.best_time_to_contact || "",
            timezone: lead.timezone || "",
            language: lead.language || "",
            address_line_1: lead.address_line_1 || lead.address || "",
            address_line_2: lead.address_line_2 || "",
            city: lead.city || "",
            state: lead.state || "",
            zip: lead.zip || "",
            county: lead.county || "",
            do_not_call: !!lead.do_not_call,
            do_not_text: !!lead.do_not_text,
            do_not_email: !!lead.do_not_email,
            do_not_mail: !!lead.do_not_mail,
            internal_warning_notes: lead.internal_warning_notes || "",
        });
        setEditOverview(true);
    };
    const saveOverview = async () => {
        const payload = { ...overviewDraft };
        if (payload.ssn_last4) payload.ssn_last4 = String(payload.ssn_last4).replace(/\D/g, "").slice(-4);
        if (await persist(payload, "Overview saved")) setEditOverview(false);
    };

    const addNote = async () => {
        if (!newNote.trim()) return;
        try {
            await api.post(`/leads/${id}/notes`, {
                note_text: newNote,
                mentioned_user_ids: Array.from(new Set(newNoteMentions.map(u => u.id))),
            });
            setNewNote("");
            setNewNoteMentions([]);
            // F4.1: tell the bell to refresh immediately
            window.dispatchEvent(new Event("notifications:refresh"));
            toast.success("Note added");
            loadNotes();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveEditedNote = async () => {
        if (!editingNote || !editingNoteText.trim()) return;
        try {
            // Lead notes update endpoint isn't built — emulate by delete + recreate.
            // (Edit is rare; PUT is in client_notes only per spec.)
            await api.delete(`/leads/${id}/notes/${editingNote.id}`);
            await api.post(`/leads/${id}/notes`, { note_text: editingNoteText });
            setEditingNote(null);
            setEditingNoteText("");
            toast.success("Note updated");
            loadNotes();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteNote = async (note) => {
        if (!confirm("Delete this note?")) return;
        try {
            await api.delete(`/leads/${id}/notes/${note.id}`);
            loadNotes();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const lastSms = (timeline || []).find(t => t.channel === "sms");
    const lastEmail = (timeline || []).find(t => t.channel === "email");
    const lastCampaign = campaigns?.[0];
    const phone = lead.phone_e164 || lead.phone_raw;

    return (
        <div className="space-y-5" data-testid="lead-detail-page">
            <Link to="/leads" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to leads
            </Link>

            {/* Header */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-2">
                        <h1 className="text-2xl font-display font-semibold text-gray-900">{lead.first_name} {lead.last_name}</h1>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            {lead.acos_id != null && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-primary/20 bg-primary/5" data-testid="header-acos-chip">
                                    <span className="text-[10px] uppercase tracking-wider text-primary/80 font-medium">A+ CaseFlow ID</span>
                                    <CopyableField value={lead.acos_id} label="A+ CaseFlow ID" mono testId="header-acos-id" />
                                </span>
                            )}
                            {lead.case_id && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-border bg-gray-50" data-testid="header-case-id-chip">
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Skywave Case ID</span>
                                    <CopyableField value={lead.case_id} label="Skywave Case ID" mono testId="header-case-id" />
                                </span>
                            )}
                            <span className="text-muted-foreground">·</span>
                            <LeadStatusPill status={lead.lead_status || "new_lead"} />
                            <Select value={lead.lead_status || "new_lead"} onValueChange={updateStatus}>
                                <SelectTrigger className="h-7 text-xs w-[140px]" data-testid="header-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{LEAD_STATUS_LABEL[s] || s}</SelectItem>)}</SelectContent>
                            </Select>
                            <div className="flex flex-wrap gap-1.5 ml-1">
                                {leadTypeObjs.length === 0 ? <LeadTypePill leadType={null} /> : leadTypeObjs.map(t => <LeadTypePill key={t.id} leadType={t} />)}
                            </div>
                            <button onClick={() => setPickerOpen(true)} className="text-xs text-primary hover:underline inline-flex items-center" data-testid="manage-types-button">
                                <Edit3 className="h-3 w-3 mr-1" /> Manage
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
                            {phone ? (
                                <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="header-phone-link">
                                    <Phone className="h-4 w-4 text-muted-foreground" />{leadPhoneDisplay(lead) || phone}
                                </a>
                            ) : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Phone className="h-4 w-4" />—</span>}
                            {lead.email ? (
                                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="header-email-link">
                                    <Mail className="h-4 w-4 text-muted-foreground" />{lead.email}
                                </a>
                            ) : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Mail className="h-4 w-4" />—</span>}
                            <span className="text-xs text-muted-foreground">Last contacted: {lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleDateString() : "—"}</span>
                            <span className="text-xs text-muted-foreground">Imported: {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}</span>
                        </div>
                    </div>
                    <div>
                        {lead.client_id ? (
                            <Link to={`/clients/${lead.client_id}`}>
                                <Button variant="outline" data-testid="view-linked-client-button">
                                    <UserCheck className="h-4 w-4 mr-1" /> View Client
                                </Button>
                            </Link>
                        ) : (
                            <Button onClick={() => setConvertOpen(true)} data-testid="convert-to-client-button">
                                <UserCheck className="h-4 w-4 mr-1" /> Convert to Client
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {!lead.processor_user_id && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2" data-testid="lead-processor-banner">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-200 text-amber-900 text-xs font-bold">!</span>
                    <span><strong>Needs Processor.</strong> Assign one in Edit Overview before converting this lead to a client.</span>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="lead-tabs">
                    <TabsTrigger value="overview" data-testid="lead-tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="notes" data-testid="lead-tab-notes">Notes</TabsTrigger>
                    <TabsTrigger value="communication" data-testid="lead-tab-communication">Communication History</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Lead File</div>
                            <div className="text-xs text-muted-foreground">All fields below save together when you click Save.</div>
                        </div>
                        {editOverview ? (
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditOverview(false)} data-testid="overview-cancel"><X className="h-4 w-4 mr-1" />Cancel</Button>
                                <Button size="sm" onClick={saveOverview} data-testid="overview-save">Save</Button>
                            </div>
                        ) : (
                            <Button size="sm" variant="outline" onClick={startEditOverview} data-testid="overview-edit"><Edit3 className="h-4 w-4 mr-1" />Edit Overview</Button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* A. Identity & Lead Info */}
                        <Card title="Identity & Lead Info" testIdRoot="identity">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="A+ CaseFlow ID" full><div className="text-sm py-2 text-muted-foreground"><CopyableField value={lead.acos_id} label="A+ CaseFlow ID" mono /> <span className="text-[10px]">(read-only)</span></div></Field>
                                    <Field label="Skywave Case ID" full><Input value={overviewDraft.case_id} onChange={e => setOverviewDraft({ ...overviewDraft, case_id: e.target.value })} data-testid="edit-case-id" /></Field>
                                    <Field label="First Name"><Input value={overviewDraft.first_name} onChange={e => setOverviewDraft({ ...overviewDraft, first_name: e.target.value })} data-testid="edit-first-name" /></Field>
                                    <Field label="Last Name"><Input value={overviewDraft.last_name} onChange={e => setOverviewDraft({ ...overviewDraft, last_name: e.target.value })} data-testid="edit-last-name" /></Field>
                                    <Field label="DOB"><Input type="date" value={overviewDraft.dob} onChange={e => setOverviewDraft({ ...overviewDraft, dob: e.target.value })} data-testid="edit-dob" /></Field>
                                    <Field label="SSN (Last 4)"><Input maxLength={11} value={overviewDraft.ssn_last4} onChange={e => setOverviewDraft({ ...overviewDraft, ssn_last4: e.target.value })} placeholder="Last 4 only" data-testid="edit-ssn4" /></Field>
                                    <Field label="Lead Status">
                                        <Select value={overviewDraft.lead_status} onValueChange={v => setOverviewDraft({ ...overviewDraft, lead_status: v })}>
                                            <SelectTrigger data-testid="edit-lead-status"><SelectValue /></SelectTrigger>
                                            <SelectContent>{LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{LEAD_STATUS_LABEL[s] || s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Source"><Input value={overviewDraft.source} onChange={e => setOverviewDraft({ ...overviewDraft, source: e.target.value })} data-testid="edit-source" /></Field>
                                    <Field label="Campaign Source"><Input value={overviewDraft.campaign_source} onChange={e => setOverviewDraft({ ...overviewDraft, campaign_source: e.target.value })} /></Field>
                                    <Field label="Assigned Rep"><Input value={overviewDraft.assigned_rep} onChange={e => setOverviewDraft({ ...overviewDraft, assigned_rep: e.target.value })} data-testid="edit-assigned-rep" /></Field>
                                    <Field label="Processor (required)">
                                        <UserAssignSelect
                                            value={overviewDraft.processor_user_id}
                                            onSelect={(u) => setOverviewDraft({
                                                ...overviewDraft,
                                                processor_user_id: u?.id || null,
                                                processor_name: u?.name || "",
                                                processor_email: u?.email || "",
                                            })}
                                            placeholder="Assign processor…"
                                            testId="edit-processor"
                                            triggerClassName="h-9 text-xs w-full"
                                        />
                                    </Field>
                                    <Field label="Sales Rep">
                                        <UserAssignSelect
                                            value={overviewDraft.sales_rep_user_id}
                                            onSelect={(u) => setOverviewDraft({
                                                ...overviewDraft,
                                                sales_rep_user_id: u?.id || null,
                                                sales_rep_name: u?.name || "",
                                                sales_rep_email: u?.email || "",
                                            })}
                                            placeholder="Assign sales rep…"
                                            testId="edit-sales-rep"
                                            triggerClassName="h-9 text-xs w-full"
                                        />
                                    </Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <LROCell label="A+ CaseFlow ID"><CopyableField value={lead.acos_id} label="A+ CaseFlow ID" mono /></LROCell>
                                    <LROCell label="Skywave Case ID">{lead.case_id ? <CopyableField value={lead.case_id} label="Case ID" mono testId="row-case-id" /> : "—"}</LROCell>
                                    <LROCell label="First Name">{lead.first_name || "—"}</LROCell>
                                    <LROCell label="Last Name">{lead.last_name || "—"}</LROCell>
                                    <LROCell label="DOB">{lead.dob || "—"}</LROCell>
                                    <LROCell label="SSN (Last 4)">{lead.ssn_last4 ? `••• ${lead.ssn_last4}` : "—"}</LROCell>
                                    <LROCell label="Lead Status"><span>{LEAD_STATUS_LABEL[lead.lead_status] || "New Lead"}</span></LROCell>
                                    <LROCell label="Lead Types">{leadTypeObjs.length ? leadTypeObjs.map(t => t.name).join(", ") : "—"}</LROCell>
                                    <LROCell label="Source">{lead.source || "—"}</LROCell>
                                    <LROCell label="Campaign Source">{lead.campaign_source || "—"}</LROCell>
                                    <LROCell label="Assigned Rep">{lead.assigned_rep || "—"}</LROCell>
                                    <LROCell label="Processor">{lead.processor_name ? <span data-testid="lead-processor-name">{lead.processor_name}{lead.processor_email ? <span className="text-xs text-muted-foreground"> — {lead.processor_email}</span> : null}</span> : <span className="text-amber-700 font-medium" data-testid="lead-needs-processor">Needs Processor</span>}</LROCell>
                                    <LROCell label="Sales Rep">{lead.sales_rep_name ? <span data-testid="lead-sales-rep-name">{lead.sales_rep_name}{lead.sales_rep_email ? <span className="text-xs text-muted-foreground"> — {lead.sales_rep_email}</span> : null}</span> : "—"}</LROCell>
                                    <LROCell label="Import Batch">{lead.import_batch_id ? <span className="font-mono text-xs">{lead.import_batch_id.slice(0, 8)}…</span> : "—"}</LROCell>
                                    <LROCell label="Created">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}</LROCell>
                                </dl>
                            )}
                        </Card>

                        {/* B. Contact Info */}
                        <Card title="Contact Info" testIdRoot="contact">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="Primary Phone"><Input value={overviewDraft.phone_raw} onChange={e => setOverviewDraft({ ...overviewDraft, phone_raw: e.target.value })} data-testid="edit-phone-raw" /></Field>
                                    <Field label="Alternate Phone"><Input value={overviewDraft.alternate_phone} onChange={e => setOverviewDraft({ ...overviewDraft, alternate_phone: e.target.value })} data-testid="edit-alt-phone" /></Field>
                                    <Field label="Email"><Input type="email" value={overviewDraft.email} onChange={e => setOverviewDraft({ ...overviewDraft, email: e.target.value })} data-testid="edit-email" /></Field>
                                    <Field label="Alternate Email"><Input type="email" value={overviewDraft.alternate_email} onChange={e => setOverviewDraft({ ...overviewDraft, alternate_email: e.target.value })} data-testid="edit-alt-email" /></Field>
                                    <Field label="Preferred Contact Method">
                                        <Select value={overviewDraft.preferred_contact_method || "none"} onValueChange={v => setOverviewDraft({ ...overviewDraft, preferred_contact_method: v === "none" ? "" : v })}>
                                            <SelectTrigger data-testid="edit-preferred-contact"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">—</SelectItem>
                                                <SelectItem value="sms">SMS</SelectItem>
                                                <SelectItem value="email">Email</SelectItem>
                                                <SelectItem value="phone">Phone</SelectItem>
                                                <SelectItem value="mail">Mail</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Best Time"><Input value={overviewDraft.best_time_to_contact} onChange={e => setOverviewDraft({ ...overviewDraft, best_time_to_contact: e.target.value })} /></Field>
                                    <Field label="Time Zone"><Input value={overviewDraft.timezone} onChange={e => setOverviewDraft({ ...overviewDraft, timezone: e.target.value })} /></Field>
                                    <Field label="Language"><Input value={overviewDraft.language} onChange={e => setOverviewDraft({ ...overviewDraft, language: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <LROCell label="Phone (raw)" mono>{lead.phone_raw || "—"}</LROCell>
                                    <LROCell label="Phone (E.164)" mono>{lead.phone_e164 ? <a href={`tel:${lead.phone_e164}`} className="text-emerald-700 hover:underline">{lead.phone_e164}</a> : <span className="text-rose-700">—</span>}</LROCell>
                                    <LROCell label="Alternate Phone" mono>{lead.alternate_phone || "—"}</LROCell>
                                    <LROCell label="Email">{lead.email ? <a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a> : "—"}</LROCell>
                                    <LROCell label="Alternate Email">{lead.alternate_email ? <a href={`mailto:${lead.alternate_email}`} className="text-primary hover:underline">{lead.alternate_email}</a> : "—"}</LROCell>
                                    <LROCell label="Preferred Contact">{lead.preferred_contact_method || "—"}</LROCell>
                                    <LROCell label="Best Time">{lead.best_time_to_contact || "—"}</LROCell>
                                    <LROCell label="Time Zone">{lead.timezone || "—"}</LROCell>
                                    <LROCell label="Language">{lead.language || "—"}</LROCell>
                                </dl>
                            )}
                        </Card>

                        {/* C. Address */}
                        <Card title="Address" testIdRoot="address">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="Address Line 1" full><Input value={overviewDraft.address_line_1} onChange={e => setOverviewDraft({ ...overviewDraft, address_line_1: e.target.value })} data-testid="edit-address-1" /></Field>
                                    <Field label="Address Line 2" full><Input value={overviewDraft.address_line_2} onChange={e => setOverviewDraft({ ...overviewDraft, address_line_2: e.target.value })} /></Field>
                                    <Field label="City"><Input value={overviewDraft.city} onChange={e => setOverviewDraft({ ...overviewDraft, city: e.target.value })} data-testid="edit-city" /></Field>
                                    <Field label="State"><Input value={overviewDraft.state} onChange={e => setOverviewDraft({ ...overviewDraft, state: e.target.value })} data-testid="edit-state" /></Field>
                                    <Field label="Zip"><Input value={overviewDraft.zip} onChange={e => setOverviewDraft({ ...overviewDraft, zip: e.target.value })} data-testid="edit-zip" /></Field>
                                    <Field label="County"><Input value={overviewDraft.county} onChange={e => setOverviewDraft({ ...overviewDraft, county: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <LROCell label="Address 1" full>{lead.address_line_1 || lead.address || "—"}</LROCell>
                                    <LROCell label="Address 2" full>{lead.address_line_2 || "—"}</LROCell>
                                    <LROCell label="City">{lead.city || "—"}</LROCell>
                                    <LROCell label="State">{lead.state || "—"}</LROCell>
                                    <LROCell label="Zip">{lead.zip || "—"}</LROCell>
                                    <LROCell label="County">{lead.county || "—"}</LROCell>
                                </dl>
                            )}
                        </Card>

                        {/* D. Contact Restrictions */}
                        <Card title="Contact Restrictions" testIdRoot="restrictions">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <LeadRestrictionToggle label="Do Not Call" testId="edit-dnc" checked={overviewDraft.do_not_call} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_call: v })} />
                                    <LeadRestrictionToggle label="Do Not Text" testId="edit-dnt" checked={overviewDraft.do_not_text} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_text: v })} />
                                    <LeadRestrictionToggle label="Do Not Email" testId="edit-dne" checked={overviewDraft.do_not_email} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_email: v })} />
                                    <LeadRestrictionToggle label="Do Not Mail" testId="edit-dnm" checked={overviewDraft.do_not_mail} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_mail: v })} />
                                    <Field label="Internal Warning Notes" full><Textarea rows={2} value={overviewDraft.internal_warning_notes} onChange={e => setOverviewDraft({ ...overviewDraft, internal_warning_notes: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <LeadRestrictionsView l={lead} />
                            )}
                        </Card>

                        <Card title="Campaign Info" testIdRoot="campaign-snap">
                            <dl className="text-sm space-y-1.5">
                                <Row label="Last Contacted">{lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleString() : "—"}</Row>
                                <Row label="Last SMS">{lastSms ? <span className="capitalize text-xs">{lastSms.status}</span> : "—"}</Row>
                                <Row label="Last Email">{lastEmail ? <span className="capitalize text-xs">{lastEmail.status}</span> : "—"}</Row>
                                <Row label="Last Campaign">{lastCampaign ? <Link to={`/campaigns/${lastCampaign.id}`} className="text-primary hover:underline">{lastCampaign.name}</Link> : "—"}</Row>
                            </dl>
                        </Card>

                        <Card title="Quick Actions">
                            <div className="flex flex-col gap-2 pt-1">
                                <Button variant="outline" size="sm" onClick={() => setTab("notes")} data-testid="quick-add-note"><FileText className="h-4 w-4 mr-1" />Add Note</Button>
                                <Button variant="outline" size="sm" onClick={() => setTab("communication")} data-testid="quick-view-comms"><MessageSquare className="h-4 w-4 mr-1" />View Communication</Button>
                                {lead.client_id ? (
                                    <Link to={`/clients/${lead.client_id}`}><Button variant="outline" size="sm" className="w-full"><UserCheck className="h-4 w-4 mr-1" />View Client</Button></Link>
                                ) : (
                                    <Button size="sm" onClick={() => setConvertOpen(true)} data-testid="quick-convert-client"><UserCheck className="h-4 w-4 mr-1" />Convert to Client</Button>
                                )}
                            </div>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="notes" className="pt-4">
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm space-y-4">
                        <div>
                            <MentionTextarea value={newNote} onChange={setNewNote} onMentionPick={(u) => setNewNoteMentions(prev => [...prev, u])} candidates={mentionCandidates} rows={3} placeholder="Add a note about this lead… Type @ to mention Processor / Sales Rep." testId="lead-new-note-input" />
                            <div className="flex justify-end mt-2">
                                <Button size="sm" onClick={addNote} disabled={!newNote.trim()} data-testid="lead-add-note-button">
                                    <Plus className="h-4 w-4 mr-1" /> Add Note
                                </Button>
                            </div>
                        </div>
                        {noteList.length === 0 ? (
                            <EmptyState title="No notes yet." help="Capture any context you want to preserve about this lead." />
                        ) : (
                            <ul className="divide-y divide-border">
                                {noteList.map(n => (
                                    <li key={n.id} className="py-3 flex items-start gap-3" data-testid={`lead-note-row-${n.id}`}>
                                        <div className="flex-1">
                                            {editingNote?.id === n.id ? (
                                                <>
                                                    <Textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} data-testid="lead-edit-note-input" />
                                                    <div className="flex gap-2 justify-end mt-2">
                                                        <Button size="sm" variant="outline" onClick={() => { setEditingNote(null); setEditingNoteText(""); }}>Cancel</Button>
                                                        <Button size="sm" onClick={saveEditedNote} data-testid="lead-save-edited-note">Save</Button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{n.note_text}</div>
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        {n.created_by_name || "—"} · {new Date(n.created_at).toLocaleString()}
                                                        {n.updated_at && ` · edited ${new Date(n.updated_at).toLocaleString()}`}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {editingNote?.id !== n.id && (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingNote(n); setEditingNoteText(n.note_text); }} data-testid={`lead-edit-note-${n.id}`}>Edit</Button>
                                                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteNote(n)}><Trash2 className="h-4 w-4" /></Button>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="communication" className="pt-4">
                    <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
                        {timeline.length === 0 ? (
                            <div className="p-5"><EmptyState title="No communication history yet." help="Outbound sends and replies tied to this lead will appear here." /></div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full text-sm table-fixed min-w-[640px]" data-testid="lead-comm-table">
                                <colgroup>
                                    <col className="w-[150px]" />
                                    <col className="w-[72px]" />
                                    <col className="w-[120px]" />
                                    <col className="w-[96px]" />
                                    <col />
                                </colgroup>
                                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2">Date</th>
                                        <th className="px-3 py-2">Channel</th>
                                        <th className="px-3 py-2">Direction</th>
                                        <th className="px-3 py-2">Status</th>
                                        <th className="px-3 py-2">Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeline.map(t => {
                                        const camp = campaigns.find(c => c.id === t.campaign_id);
                                        return (
                                            <tr key={t.id} className="border-t border-border align-top" data-testid={`lead-comm-row-${t.id}`}>
                                                <td className="px-3 py-2 text-xs text-gray-700">{new Date(t.created_at).toLocaleString()}</td>
                                                <td className="px-3 py-2 text-xs capitalize">{t.channel}</td>
                                                <td className="px-3 py-2"><DirectionBadge comm={t} testId={`lead-comm-direction-${t.id}`} /></td>
                                                <td className="px-3 py-2 capitalize text-xs" data-testid={`lead-comm-status-${t.id}`}>{t.status || "—"}</td>
                                                <td className="px-1 py-1.5">
                                                    <CommMessageCell comm={t} testId={`lead-comm-body-${t.id}`} />
                                                    <CommDetails items={commDetailItems(t, camp)} testId={`lead-comm-details-${t.id}`} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            <LeadTypesPickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                types={types}
                initialSelected={leadTypeIds}
                title="Manage lead types"
                description="Select all types this lead should belong to."
                confirmLabel="Save"
                onConfirm={saveTypes}
            />
            <ConvertLeadDialog
                open={convertOpen}
                onOpenChange={setConvertOpen}
                lead={lead}
                leadTypeNames={leadTypeObjs.map(t => t.name)}
            />
        </div>
    );
}

function Card({ title, editing, onEdit, onCancel, onSave, testIdRoot, children }) {
    return (
        <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-900">{title}</div>
                {onEdit && (editing ? (
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={onCancel} data-testid={testIdRoot ? `${testIdRoot}-cancel` : undefined}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
                        <Button size="sm" onClick={onSave} data-testid={testIdRoot ? `${testIdRoot}-save` : undefined}>Save</Button>
                    </div>
                ) : (
                    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={testIdRoot ? `${testIdRoot}-edit` : undefined}><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                ))}
            </div>
            {children}
        </div>
    );
}

function Row({ label, children, mono = false }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
            <dd className={`text-right text-gray-800 ${mono ? "font-mono text-xs" : ""}`}>{children || "—"}</dd>
        </div>
    );
}

function Field({ label, children, full = false }) {
    return (
        <label className={`block space-y-1 ${full ? "col-span-2" : ""}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
        </label>
    );
}

function LROCell({ label, children, full = false, mono = false }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
            <div className={`text-sm text-gray-900 ${mono ? "font-mono text-xs" : ""}`}>{children || <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}

function LeadRestrictionToggle({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center gap-2 text-sm py-1 cursor-pointer select-none" data-testid={testId}>
            <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="h-4 w-4 accent-rose-600" />
            <span>{label}</span>
        </label>
    );
}

function LeadRestrictionsView({ l }) {
    const flags = [
        l.do_not_call && "Do Not Call",
        l.do_not_text && "Do Not Text",
        l.do_not_email && "Do Not Email",
        l.do_not_mail && "Do Not Mail",
    ].filter(Boolean);
    if (!flags.length && !l.internal_warning_notes) {
        return <div className="text-xs text-muted-foreground italic" data-testid="no-restrictions">No contact restrictions.</div>;
    }
    return (
        <div className="space-y-2" data-testid="restrictions-view">
            {flags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {flags.map(f => (
                        <span key={f} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200" data-testid={`restriction-${f.toLowerCase().replace(/ /g, "-")}`}>
                            {f}
                        </span>
                    ))}
                </div>
            )}
            {l.internal_warning_notes && <div className="text-xs text-gray-700">{l.internal_warning_notes}</div>}
        </div>
    );
}


function EmptyState({ title, help }) {
    return (
        <div className="text-center py-8 text-muted-foreground">
            <div className="text-sm font-medium text-gray-700">{title}</div>
            <div className="text-xs mt-1">{help}</div>
        </div>
    );
}

